import { TFunction } from "i18next";
import {
  INotification,
  NotificationAssetType,
  NotificationCTAType,
} from "models/notificationItem.models";
import { NotificationTemplateType } from "utils/constants/notifications.constants";
import { Path } from "utils/constants/paths.constants";
import { ProjectStatus } from "utils/constants/project.constants";
import { getDocumentPath, getHAQPath } from "utils/helpers";
import {
  getDisplayedAssetStatus,
  getDisplayedProjectStatus,
  TPossibleAssetStatus,
} from "utils/helpers/getDisplayedStatus";
import { FolderIcon, GlobeLocatorIcon } from "assets/icons";

export const getReadableAssetType = (
  assetType: NotificationAssetType,
  t: TFunction,
) => {
  const assetTypeMap: { [key in NotificationAssetType]: string } = {
    [NotificationAssetType.Document]: t(
      "navigation.notifications.notificationAssetTypes.document",
    ),
    [NotificationAssetType.Component]: t(
      "navigation.notifications.notificationAssetTypes.component",
    ),
    [NotificationAssetType.SupportingInformation]: t(
      "navigation.notifications.notificationAssetTypes.supportingInformation",
    ),
    [NotificationAssetType.Comment]: t(
      "navigation.notifications.notificationAssetTypes.comment",
    ),
    [NotificationAssetType.Question]: t(
      "navigation.notifications.notificationAssetTypes.question",
    ),
    [NotificationAssetType.QuestionResponse]: t(
      "navigation.notifications.notificationAssetTypes.questionResponse",
    ),
    [NotificationAssetType.Product]: t(
      "navigation.notifications.notificationAssetTypes.product",
    ),
    [NotificationAssetType.ProductFamily]: t(
      "navigation.notifications.notificationAssetTypes.productFamily",
    ),
    [NotificationAssetType.Substance]: t(
      "navigation.notifications.notificationAssetTypes.substance",
    ),
    [NotificationAssetType.AssessmentReport]: t(
      "navigation.notifications.notificationAssetTypes.assessmentReport",
    ),
    [NotificationAssetType.ImportedHAQ]: t(
      "navigation.notifications.notificationAssetTypes.questions",
    ),
    [NotificationAssetType.HADecisionLetter]: t(
      "navigation.notifications.notificationAssetTypes.HADecisionLetter",
    ),
    [NotificationAssetType.ECTD]: t(
      "navigation.notifications.notificationAssetTypes.ECTD",
    ),
    [NotificationAssetType.ApplicantsStrategy]: t(
      "navigation.notifications.notificationAssetTypes.applicantsStrategy",
    ),
    [NotificationAssetType.CoverLetter]: t(
      "navigation.notifications.notificationAssetTypes.coverLetter",
    ),
    [NotificationAssetType.InspectionReports]: t(
      "navigation.notifications.notificationAssetTypes.inspectionReports",
    ),
    [NotificationAssetType.ReferenceDocument]: t(
      "navigation.notifications.notificationAssetTypes.referenceDocument",
    ),
    [NotificationAssetType.StructuredDocument]: t(
      "navigation.notifications.notificationAssetTypes.structuredDocument",
    ),
    [NotificationAssetType.StructuredDocumentChild]: t(
      "navigation.notifications.notificationAssetTypes.structuredDocument",
    ),
  };

  return assetTypeMap[assetType] || assetType;
};

// This will eventually be expanded when other notification types are supported
export const getReadableCTAType = (
  notification: INotification,
  t: TFunction,
): string => {
  const { metadata, templateType } = notification;
  const { ctaType, assetType } = metadata;

  if (ctaType === NotificationCTAType.Project) {
    return t("navigation.notifications.notificationCTATypes.project");
  }

  if (ctaType === NotificationCTAType.Task) {
    return t("navigation.notifications.notificationCTATypes.task");
  }

  if (ctaType === NotificationCTAType.Asset && assetType) {
    if (templateType === NotificationTemplateType.DocumentCopy) {
      return t("navigation.notifications.notificationCTATypes.document");
    }

    return getReadableAssetType(assetType, t);
  }

  if (ctaType === NotificationCTAType.HAQs) {
    return t("navigation.notifications.notificationCTATypes.questions");
  }

  if (ctaType === NotificationCTAType.Content) {
    const { numOfDocuments, documentNames } = metadata;

    return documentNames && numOfDocuments && Number(numOfDocuments) <= 1
      ? documentNames.slice(1, -1).split(",")[0]
      : t("navigation.notifications.notificationCTATypes.content");
  }

  return "";
};

export const getCTALink = (notification: INotification): string => {
  const { metadata, templateType } = notification;

  const { ctaType, assetType, assetId, projectId, parentAssetId, groupId } =
    metadata;

  if (ctaType === NotificationCTAType.Project && projectId) {
    return Path.Project.replace(":projectId", projectId ?? "");
  }

  if (ctaType === NotificationCTAType.Task && groupId) {
    return Path.Task.replace(":taskGroupId", groupId ?? "");
  }

  if (ctaType === NotificationCTAType.HAQs) {
    return Path.ProjectHAQs.replace(":projectId", projectId ?? "");
  }

  if (ctaType === NotificationCTAType.Content) {
    return Path.ProjectDocuments.replace(":projectId", projectId ?? "");
  }

  if (ctaType === NotificationCTAType.Asset) {
    // This will eventually be expanded with other values
    switch (assetType) {
      case NotificationAssetType.Question:
        return getHAQPath(projectId, assetId);

      case NotificationAssetType.QuestionResponse:
        return parentAssetId ? getHAQPath(projectId, parentAssetId) : "";

      case NotificationAssetType.ImportedHAQ:
        return templateType !==
          NotificationTemplateType.AssetHAQImportParsingFail
          ? Path.ProjectHAQs.replace(":projectId", projectId ?? "")
          : "";

      default:
        return getDocumentPath(projectId, {
          id: assetId,
          type: assetType,
        });
    }
  }

  return "";
};

export const getNotificationIcon = (type: NotificationCTAType) => {
  // More notification icon types will be supported in the future
  switch (type) {
    case NotificationCTAType.Task:
      return <GlobeLocatorIcon data-testid="notification-task-icon" />;
    default:
      return <FolderIcon data-testid="notification-icon" />;
  }
};

export const getNotificationTitleForReason = (
  { templateType, metadata }: INotification,
  t: TFunction,
) => {
  if (
    templateType ===
      NotificationTemplateType.ProjectRelianceStatusUpdateWithStateAndReason &&
    metadata.targetStatus === ProjectStatus.Cancelled
  ) {
    return t("navigation.notifications.reasonForCancellation");
  }

  if (
    templateType ===
    NotificationTemplateType.ProjectRelianceStatusUpdateWithStateAndReasonRevert
  ) {
    return t("navigation.notifications.reasonForReversion");
  }

  return t("navigation.notifications.reason");
};

export const getDisplayedNotificationStatus = (
  type: NotificationCTAType,
  status: ProjectStatus | TPossibleAssetStatus,
  t: TFunction,
) => {
  return type === NotificationCTAType.Project
    ? getDisplayedProjectStatus(status as ProjectStatus, t)
    : getDisplayedAssetStatus(status as TPossibleAssetStatus, t);
};
